import React, {
  ChangeEvent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import moment, { Moment } from 'moment'
import {
  BiChevronsLeft as ChevronsLeft,
  BiChevronLeft as ChevronLeft,
  BiChevronRight as ChevronRight,
  BiChevronsRight as ChevronsRight,
  BiX as CloseIcon,
} from 'react-icons/bi'
import { FiCalendar as CalendarIcon } from 'react-icons/fi'
import FieldStyles, { FieldSize } from '../components/FieldStyles'
import colors from '../lib/colors'
import { hasForbiddenChars } from '../helpers/fieldHelper'

const StyledCalendarIcon = styled(CalendarIcon)`
  stroke: ${colors.darkGray};
  height: 17px;
  width: 17px;
  position: absolute;
  bottom: 7px;
  margin: auto 0;
  right: 7px;
  pointer-events: none;
`
const StyledCloseIcon = styled(CloseIcon)`
  color: ${colors.darkGray};
  height: 21px;
  width: 21px;
  position: absolute;
  bottom: 4px;
  margin: auto 0;
  right: 4px;
  cursor: pointer;
`
const Dropdown = styled.div`
  .picker-icon {
    color: ${colors.darkerGray};
    cursor: pointer;
    height: 30px;
    width: 30px;
    margin-top: 3px;
    &.picker-icon-thin {
      margin: 0 -5px;
    }
    :hover {
      color: ${colors.darkestGray};
    }
    &.icon-disabled {
      color: ${colors.gray};
    }
  }
`
const Header = styled.div`
  height: 35px;
  width: 240px;
  background-color: ${colors.lightGray};
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  user-select: none;
`
const Month = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: ${colors.white};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  padding: 5px;
  gap: 3px;
`
const Day = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  :hover {
    background-color: ${colors.gray};
  }
  &.day-active {
    background-color: ${(props): string => (props.theme.length ? props.theme : colors.lightBlue)};
    color: ${colors.white};
  }
  &.day-disabled {
    pointer-events: none;
    color: ${colors.gray};
  }
  &.day-today {
    font-weight: bold;
  }
`
const DayLabels = styled.div`
  background-color: ${colors.white};
  display: grid;
  padding: 0 5px;
  gap: 3px;
  grid-template-columns: repeat(7, 1fr);
  padding-top: 7px;
`
const DayLabel = styled.div`
  font-weight: bold;
  color: ${colors.darkerGray};
  font-size: 0.9rem;
  text-align: center;
`
const Wrapper = styled(FieldStyles)`
  position: relative;
  ${(props): string => {
    if (props.fieldSize === FieldSize.Large) {
      return `
      ${Header} { width: 240px; }
      ${StyledCalendarIcon} { bottom: 10px; }
      `
    }
    return ''
  }}
  min-width: 110px;
`

export enum DatepickerFormat {
  Readable = 'D MMM YYYY',
  International = 'DD/MM/YYYY',
  Long = 'DD MMMM YYYY',
}

export interface IDatepickerProps {
  placeholder?: string
  isError?: boolean
  disabled?: boolean
  isLong?: boolean
  futureOnly?: boolean
  onChange: (date: Moment | null) => void
  value: Moment | null
  label?: string
  dateFormat?: DatepickerFormat
  isClearable?: boolean
  maxDate?: Moment
  customColor?: string
  fieldSize?: FieldSize
}
function Datepicker(props: IDatepickerProps): ReactElement {
  const {
    placeholder,
    disabled,
    isError,
    isLong,
    futureOnly,
    onChange,
    value,
    label,
    dateFormat = DatepickerFormat.International,
    isClearable,
    maxDate,
    customColor,
    fieldSize,
  } = props
  const [menu, setMenu] = useState(false)

  const initDate = value && value.isValid() ? value : null
  const [selected, setSelected] = useState(initDate)

  const initPicker = value && value.isValid() ? value.clone().startOf('month') : moment()
  const [pickerStartDate, setPickerStartDate] = useState(initPicker)

  const [userTypedInput, setUserTypedInput] = useState<string | false>(false)
  const [userInputDate, setUserInputDate] = useState<Moment | false>(false)

  const [formatError, setFormatError] = useState(false)

  useEffect(() => {
    // if the value is changed externally the selected value should update to reflect this
    // and the start date should reflect the new value too
    setSelected(initDate)
    setPickerStartDate(initPicker)
  }, [value])

  useEffect(() => {
    // if the picker is closed, the picker start date should reset
    if (!menu) setPickerStartDate(userInputDate || initPicker)
  }, [menu])

  const canGoBackYear = !futureOnly || moment().add(1, 'y') < pickerStartDate.clone().add(1, 'M')
  const canGoBackMonth = !futureOnly || moment() < pickerStartDate

  const renderHeader = (): ReactElement => (
    <Header>
      <div>
        <ChevronsLeft
          className={`picker-icon ${!canGoBackMonth ? 'icon-disabled' : ''}`}
          onClick={(): void => {
            if (canGoBackYear) {
              setPickerStartDate((prevState) => prevState.clone().subtract(1, 'y'))
            } else {
              setPickerStartDate((prevState) => prevState
                .clone()
                .set('year', moment().year())
                .set('month', moment().month()))
            }
          }}
        />
        <ChevronLeft
          className={`picker-icon picker-icon-thin ${!canGoBackMonth ? 'icon-disabled' : ''
            }`}
          onClick={(): void => {
            if (canGoBackMonth) {
              setPickerStartDate((prevState) => prevState.clone().subtract(1, 'M'))
            }
          }}
        />
      </div>
      {pickerStartDate.clone().format('MMMM YYYY')}
      <div>
        <ChevronRight
          className="picker-icon picker-icon-thin"
          onClick={(): void => setPickerStartDate((prevState) => prevState.clone().add(1, 'M'))}
        />
        <ChevronsRight
          className="picker-icon"
          onClick={(): void => setPickerStartDate((prevState) => prevState.clone().add(1, 'y'))}
        />
      </div>
    </Header>
  )

  const renderDayLabels = (): ReactElement => {
    const labelArray = []
    const thisMonth = pickerStartDate.clone().startOf('month')
    for (let i = 0; i < 7; i += 1) {
      const day = (
        <DayLabel key={i}>
          {thisMonth.clone().add(i, 'd').format('dd')}
        </DayLabel>
      )
      labelArray.push(day)
    }
    return <DayLabels>{labelArray}</DayLabels>
  }

  const dayString = (date: Moment | null): string | null => date && date.clone().startOf('day').toString()

  const renderMonth = (): ReactElement => {
    const dayArray = []
    const thisMonth = pickerStartDate.clone().startOf('month')
    for (let i = 1; i < thisMonth.daysInMonth() + 1; i += 1) {
      const thisDay = thisMonth.clone().add(i - 1, 'day')

      const isDisabledInPast = futureOnly && moment().startOf('day') > thisDay
      const isDisabledInFuture = maxDate && maxDate < thisDay

      const isDayDisabled = isDisabledInPast || isDisabledInFuture
      const isDayActive = dayString(selected) === dayString(thisDay)
      const isDayToday = dayString(thisDay) === dayString(moment())

      const activeClass = isDayActive ? 'day-active' : ''
      const disabledClass = isDayDisabled ? 'day-disabled' : ''
      const todayClass = isDayToday ? 'day-today' : ''

      const day = (
        <Day
          key={i}
          className={`${activeClass} ${disabledClass} ${todayClass}`}
          theme={customColor}
          onClick={(): void => {
            setMenu(false)
            setSelected(thisDay)
            setPickerStartDate(thisDay)
            onChange(thisDay)
            setUserTypedInput(false)
            setFormatError(false)
          }}
        >
          {i}
        </Day>
      )
      dayArray.push(day)
    }
    return <Month>{dayArray}</Month>
  }

  // set up a ref on a div that wraps both our input and our dropdown
  const fieldWrapperRef = useRef<HTMLDivElement>(null)
  const handleClickOutside = (mouseDownEvent: MouseEvent): void => {
    const ref = fieldWrapperRef.current
    const { target } = mouseDownEvent
    // if our ref doesn't contain the click target, close the menu
    if (target instanceof Node && ref && !ref.contains(target)) setMenu(false)
  }
  const handleEscape = (keyDownEvent: KeyboardEvent): void => {
    if (keyDownEvent.code === 'Escape') setMenu(false)
  }
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [fieldWrapperRef])

  const formatClass = formatError ? 'error' : ''
  const errorClass = isError ? 'error' : ''
  const longClass = isLong ? 'input-long' : ''

  return (
    <Wrapper label={label} fieldSize={fieldSize}>
      <div ref={fieldWrapperRef}>
        <input
          type="text"
          className={`styled-input ${formatClass} ${errorClass} ${longClass}`}
          onClick={(): void => setMenu(true)}
          onChange={(event: ChangeEvent<HTMLInputElement>): void => {
            if (hasForbiddenChars(event.target.value)) return

            const userInput = moment(event.target.value, dateFormat, true)
            const isDisabledInPast = futureOnly && moment().startOf('day') > userInput
            const isDisabledInFuture = maxDate && maxDate < userInput
            if (
              userInput.isValid()
              && !isDisabledInPast
              && !isDisabledInFuture
            ) {
              setSelected(userInput)
              setPickerStartDate(userInput)
              onChange(userInput)
              setUserInputDate(userInput)
              setUserTypedInput(false)
              setFormatError(false)
            } else {
              setUserTypedInput(event.target.value)
              setFormatError(true)
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          value={userTypedInput || selected?.format(dateFormat) || ''}
        />
        {isClearable && selected ? (
          <StyledCloseIcon
            onClick={(): void => {
              setSelected(null)
              setPickerStartDate(initPicker)
              onChange(null)
              setUserInputDate(false)
              setUserTypedInput(false)
            }}
          />
        ) : (
          <StyledCalendarIcon />
        )}
        {menu ? (
          <Dropdown className="dropdown">
            {renderHeader()}
            {renderDayLabels()}
            {renderMonth()}
          </Dropdown>
        ) : null}
      </div>
    </Wrapper>
  )
}
export default Datepicker
